import { Fragment, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, Plus, Scale, Search, Settings2 } from 'lucide-react'
import { getAccounts, getControlAccounts } from '../../api/master'
import { getStatementClasses } from '../../api/accounting'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { HelpTip } from '../../components/HelpTip'
import { FilterChips } from '../../components/PageParts'
import { downloadCsv, acct, glHref } from '../accounting/reportsShared'
import { ACCOUNT_TYPES, DEFAULT_LABEL, MasterTabs } from './masterShared'
import { ChartGuide } from './MasterGuides'

// Chart of accounts: every account in the books, under its heading
// account, with its balance, where it shows on the statements and what
// the app posts to it.
export default function AccountList() {
  const [params, setParams] = useSearchParams()
  const type = ACCOUNT_TYPES.some(([t]) => t === params.get('type')) ? params.get('type') : 'all'
  const [search, setSearch] = useState('')
  const { data: accounts = [], isLoading, error } = useQuery({ queryKey: ['accounts'], queryFn: getAccounts })
  const { data: defaults = [] } = useQuery({ queryKey: ['control-accounts'], queryFn: getControlAccounts })
  const { data: classes } = useQuery({ queryKey: ['statement-classes'], queryFn: getStatementClasses })

  const rolesByCode = useMemo(() => {
    const map = {}
    for (const d of defaults) {
      const code = String(d.account_code || '').trim()
      if (code) (map[code] = map[code] || []).push(DEFAULT_LABEL[d.name] || d.name)
    }
    return map
  }, [defaults])
  const sectionByCode = useMemo(() => Object.fromEntries((classes?.accounts || []).map((a) => [a.code, a.section])), [classes])
  const counts = useMemo(() => Object.fromEntries([['all', accounts.length], ...ACCOUNT_TYPES.map(([t]) => [t, accounts.filter((a) => a.type === t).length])]), [accounts])

  // Heading accounts with the accounts under them; a search keeps a
  // heading when it or any account under it matches.
  const groups = useMemo(() => {
    const term = search.trim().toLowerCase()
    const match = (a) => !term || `${a.account_code} ${a.account_name} ${(rolesByCode[a.account_code] || []).join(' ')}`.toLowerCase().includes(term)
    const tops = accounts.filter((a) => !a.parent_account_id && (type === 'all' || a.type === type))
    return tops
      .map((top) => {
        const kids = accounts.filter((a) => a.parent_account_id === top.account_id)
        const shownKids = match(top) ? kids : kids.filter(match)
        return { top, kids: shownKids, show: match(top) || shownKids.length > 0 }
      })
      .filter((g) => g.show)
  }, [accounts, type, search, rolesByCode])

  const exportCsv = () => downloadCsv('chart_of_accounts', [
    ['Code', 'Name', 'Type', 'Group', 'Statement section', 'Default for', 'Balance'],
    ...accounts.map((a) => [a.account_code, a.account_name, a.type, accounts.find((p) => p.account_id === a.parent_account_id)?.account_code || '',
      sectionByCode[a.account_code] || '', (rolesByCode[a.account_code] || []).join('; '), Number(a.current_balance || 0).toFixed(2)]),
  ])

  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Chart of accounts <HelpTip text="Every account your books use. Heading accounts (codes ending 00) group the accounts under them; entries are posted to the accounts under a heading." /></h1>
            <div className="ci-title-meta">Every account in your books, grouped under its heading.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-ghost" to="/master/control-accounts"><Settings2 size={15} />Transaction accounts</Link>
          <Link className="ci-btn ci-btn-ghost" to="/master/opening-balances"><Scale size={15} />Opening balances</Link>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!accounts.length}><Download size={15} />Export</button>
          <Link className="ci-btn ci-btn-primary" to="/master/accounts/new"><Plus size={15} />New account</Link>
        </div>
      </div>

      <ChartGuide />

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search accounts">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code, name or default…" />
        </label>
      </div>
      <FilterChips options={[{ id: 'all', label: 'All', count: counts.all }, ...ACCOUNT_TYPES.map(([t, label]) => ({ id: t, label, count: counts[t] }))]} value={type}
        onChange={(id) => setParams((p) => { const n = new URLSearchParams(p); if (id === 'all') n.delete('type'); else n.set('type', id); return n }, { replace: true })} />

      {error && <ErrorBanner message="Couldn’t load the chart of accounts." />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead><tr><th>Account</th><th className="hide-sm">Statement section</th><th className="num"><span className="label-with-help">Balance<HelpTip k="ledgerBalance" /></span></th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="3"><Spinner label="Loading accounts…" /></td></tr>
              ) : groups.length === 0 ? (
                <tr><td colSpan="3"><div className="ci-empty">{accounts.length ? 'No accounts match.' : <>No accounts yet. <Link to="/master/setup">Set up your accounts</Link>.</>}</div></td></tr>
              ) : groups.map(({ top, kids }) => (
                <Fragment key={top.account_id}>
                  <AccountRow a={headingTotal(top, accounts)} roles={rolesByCode[top.account_code]} section={sectionByCode[top.account_code]} />
                  {kids.map((k) => <AccountRow key={k.account_id} a={k} sub roles={rolesByCode[k.account_code]} section={sectionByCode[k.account_code]} />)}
                  {!search.trim() && (
                    <tr className="mf-acct-add"><td colSpan="3"><Link to={`/master/accounts/new?parent=${top.account_id}`}><Plus size={12} /> Add an account under {top.account_name}</Link></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{accounts.length} accounts</span>
          <Link className="ci-page-info" to="/accounting/reports/classification">Statement sections</Link>
        </div>
      </div>
    </div>
  )
}

function AccountRow({ a, sub, roles = [], section }) {
  return (
    <tr className={sub ? '' : 'mf-acct-head'}>
      <td className={sub ? 'mf-acct-sub' : ''}>
        <Link className="mf-row-link" to={`/master/accounts/${a.account_id}`}><span className="mf-acct-code">{a.account_code}</span>{a.account_name}</Link>
        {roles.length > 0 && <span className="mf-roles mf-acct-roles">{roles.map((r) => <span key={r} className="mf-role">{r}</span>)}</span>}
      </td>
      <td className="hide-sm ci-muted-cell">{section || a.type}</td>
      <td className="num">{a.has_postings ? <Link className="fs-link" to={glHref(a.account_code)}>{acct(a.current_balance)}</Link> : <span className="ci-muted-cell">–</span>}</td>
    </tr>
  )
}

/** A heading shows its group's total: its own postings plus the accounts under it. */
function headingTotal(top, accounts) {
  const kids = accounts.filter((a) => a.parent_account_id === top.account_id)
  if (!kids.length) return top
  return {
    ...top,
    has_postings: top.has_postings || kids.some((k) => k.has_postings),
    current_balance: Math.round([top, ...kids].reduce((sum, a) => sum + Number(a.current_balance || 0), 0) * 100) / 100,
  }
}
